import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PropertyModal from '../../Components/PropertyCard/PropertyModal';
import api from '../../api/axiosConfig';
import { FaHome, FaPlusSquare, FaUser, FaShare } from 'react-icons/fa';
import { Clock, Heart, MapPin, BedDouble } from 'lucide-react';
import { Explore } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { stringToColor } from '../../utils/avatarColor';
import  { getCSRFToken } from '../../utils/auth';

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-screen bg-[var(--bg-black)]">
      <div className="relative">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-200"></div>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-500 border-t-transparent absolute top-0 left-0"></div>
      </div>
    </div>
  );
}

export default function UserProfileDash() {
  const { id } = useParams(); 
  const [user, setUser] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // State for Property Modal
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { t} = useTranslation('Userprofiledash');
  const { t: tCommon } = useTranslation('translation');

  const [userCurrency, setUserCurrency] = useState('RWF');
  const [fxRates, setFxRates] = useState({});

  // Fetch user data and their properties
  useEffect(() => {
    let isCancelled = false;

    const fetchUserData = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/profile/${id}/`);

        if (!isCancelled) {
          setUser(res.data.user);      
          setProperties(res.data.properties || []); 
        }
      } catch (err) {
        console.error('Failed to load user profile:', err);
        if (!isCancelled) {
          setUser(null);
          setProperties([]);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchUserData();

    return () => {
      isCancelled = true;
    };
  }, [id]);

  // Fetch follow status
  useEffect(() => {
    if (!user?.id) return;

    const fetchFollowStatus = async () => {
      try {
        const res = await api.get(`/users/${user.id}/follow_status/`);
        setIsFollowing(res.data.is_following || false);
      } catch (err) {
        console.warn('Could not fetch follow status');
      }
    };

    fetchFollowStatus();
  }, [user?.id]);


  useEffect(() => {
  const savedCurrency = localStorage.getItem('preferredCurrency') || 'RWF';
  setUserCurrency(savedCurrency);

  const fetchFxRates = async () => {
    try {
      const fxRes = await api.get('/fx-rates/');
      setFxRates(fxRes.data);
    } catch (err) {
      console.error('Failed to fetch FX rates in UserProfileDash:', err);
    }
  };

  fetchFxRates();
}, []);

  // Toggle Follow/Unfollow
  const toggleFollow = async () => {
    if (!user?.id) return;

    try {
      const csrfToken = await getCSRFToken();

      if (isFollowing) {
        await api.post(
          `/users/${user.id}/unfollow/`,
          {},
          {
            withCredentials: true,
            headers: { 'X-CSRFToken': csrfToken },
          }
        );
        setIsFollowing(false);
      } else {
        await api.post(
          `/users/${user.id}/follow/`,
          {},
          {
            withCredentials: true,
            headers: { 'X-CSRFToken': csrfToken },
          }
        );
        setIsFollowing(true);
      }
    } catch (err) {
      console.error(t('profile.errors.followFailed'), err);
    }
  };


  const formatPrice = (priceRwf) => {
  // If no FX data or user hasn't changed currency, show RWF
  if (!fxRates || !userCurrency || userCurrency === 'RWF') {
    const num = Number(priceRwf);
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M RWF`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(0)}K RWF`;
    return `${num} RWF`;
  }

  // Convert from RWF 
  const rwfRate = fxRates['RWF'];
  const targetRate = fxRates[userCurrency];

  if (!rwfRate || !targetRate) {
    return `${formatNumber(priceRwf)} RWF`; // fallback
  }

  const priceInUsd = priceRwf / rwfRate;
  const priceInTarget = priceInUsd * targetRate;
  const rounded = Math.round(priceInTarget);

  return `${formatNumber(rounded)} ${userCurrency}`;
};

const formatNumber = (num) => {
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`.replace(/\.0$/, '');
  }
  return num.toString();
};

const handleShareProfile = async () => {
  const shareUrl = `${window.location.origin}/user/${user.id}`;

  try {
    // Use Web Share API if available (mobile/desktop modern browsers)
    if (navigator.share) {
      await navigator.share({
        title: `${user.username}'s Profile`,
        text: `Check out ${user.username}'s property listings on Pongo!`,
        url: shareUrl,
      });
      return;
    }
  } catch (err) {
    if (err.name !== 'AbortError') {
      console.warn('Web Share failed:', err);
    }
  }

  // Fallback: Copy to clipboard
  try {
    await navigator.clipboard.writeText(shareUrl);
    alert(t('profile.share.success'));
  } catch (err) {
    // Final fallback: show prompt
    const dummy = document.createElement('input');
    document.body.appendChild(dummy);
    dummy.value = shareUrl;
    dummy.select();
    document.execCommand('copy');
    document.body.removeChild(dummy);
    alert('Profile link copied to clipboard!');
  }
};

  const openPropertyModal = (property) => {
    if (properties.length === 0) return; 
    setSelectedProperty(property);
    setIsModalOpen(true);
  };

  const closePropertyModal = () => {
    setIsModalOpen(false);
    setSelectedProperty(null);
  };

  const handleMessageOwner = (property) => {
    if (!property || !property.id) {
      console.warn('Invalid property passed to handleMessageOwner');
      return;
    }
    navigate(`/messages?property_id=${property.id}`);
    closePropertyModal();
  };

  if (loading) return <LoadingSpinner />;
  if (!user) return <div className="text-white text-center py-20">{t('profile.user.notFound')}</div>;

  return (
    <div className={`min-h-screen bg-[var(--bg-black)] transition-colors duration-300`}>
      {/* Top Header - Sticky */}
      <header className="sticky top-0 z-50 bg-[var(--bg-black)] border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: Back Button or Logo */}
            <Link
              to="/homedash"
              className="flex items-center gap-2 text-[var(--text-black)] hover:text-pink-500 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="hidden sm:inline">{t('profile.header.back')}</span>
            </Link>
            <button
              onClick={() => navigate('/profiledash')}
              className="px-4 py-2 bg-pink-500 border-none text-white rounded-full text-sm font-medium hover:bg-pink-600 transition"
            >
              {t('profile.header.editProfile')}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* User Profile Header */}
        <div className="bg-[var(--bg-black)] border border-white/40 border-solid rounded-xl p-6 mb-6 flex flex-col md:flex-row items-start md:items-center gap-6">
          {/* Avatar */}
          {(() => {
            const displayName = user.username || 'U';
            const initials = displayName.charAt(0).toUpperCase();
            const bgColor = stringToColor(user.username || user.id || 'user').substring(1);
            const avatarUrl = user.avatar_url || 
              `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=${bgColor}&color=fff&size=250`;
            return (
              <img
                src={avatarUrl}
                alt={user.username || 'User'}
                className="w-24 h-24 rounded-full border-4 border-white shadow-lg"
              />
            );
          })()}
          {/* Info & Stats */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-4 mb-3">
              <h1 className="text-2xl font-bold text-[var(--text-black)]">{user.username}</h1>
              {user.verified && (
                <svg className="w-6 h-6 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
              )}
            </div>

            {user.phone_number ? (
              <p className="text-sm text-gray-400 mb-4 flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.552 21 3 14.448 3 6.5V5z" />
                </svg>
                +{user.phone_number}
              </p>
            ) : (
              <p className="text-sm text-gray-400 mb-4">{tCommon('profile.user.noPhone')}</p>
            )}

            {/* Stats */}
            <div className="flex items-center gap-6 mb-4">
              <div className="text-center">
                <div className="font-bold text-[var(--text-black)]">{user.following_count || 0}</div>
                <div className="text-xs text-gray-400">{t('profile.user.stats.following')}</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-[var(--text-black)]">{user.followers_count || 0}</div>
                <div className="text-xs text-gray-400">{t('profile.user.stats.followers')}</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-[var(--text-black)]">{user.likes_count || 0}</div>
                <div className="text-xs text-gray-400">{t('profile.user.stats.likes')}</div>
              </div>
            </div>
            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={toggleFollow}
                className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${
                  isFollowing
                    ? 'bg-gray-500 text-white hover:bg-gray-600 border-none'
                    : 'bg-pink-500 text-white hover:bg-pink-600 border-none'
                }`}
              >
                {isFollowing ? t('profile.user.actions.following') : t('profile.user.actions.follow')}
              </button>
              <button
                onClick={() => navigate(`/messages?user_id=${user.id}`)}
                className="px-6 py-2 bg-[var(--bg-black)] border border-white/40 border-solid text-[var(--text-black)] rounded-full text-sm font-bold hover:bg-gray-800 transition"
              >
                {t('profile.user.actions.message')}
              </button>
              <button
                onClick={handleShareProfile}
                className="px-6 py-2 bg-[var(--bg-black)] border border-white/40 border-solid text-[var(--text-black)] rounded-full text-sm font-bold hover:bg-gray-800 transition"
              >
                <FaShare size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Property Grid */}
        <div className="mb-6 ">
          <h2 className="text-xl font-bold text-[var(--text-black)]  mb-4">{t('profile.listings.title')}</h2>
          {properties.length === 0 ? (
            <div className="text-center py-20 bg-[var(--bg-black)] rounded-xl shadow-sm">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-2 13a2 2 0 00-2-2V7m2 13a2 2 0 01-2-2V9a2 2 0 012-2h2m-2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2" />
              </svg>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {t('profile.listings.empty.title')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {t('profile.listings.empty.description')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {properties.map((property) => (
                <article
                  key={property.id}
                  className="bg-[var(--bg-black)] rounded-xl property-card-shadow overflow-hidden rounded-xl shadow-md  hover:shadow-xl transition-shadow duration-300 cursor-pointer"
                  onClick={() => openPropertyModal(property)}
                >
                  {/* Property Image */}
                  <div className="relative bg-[var(--bg-black)]">
                    <img
                      src={property.images?.length > 0 ? property.images[0].image : '/fallback.jpg'}
                      alt={property.title}
                      className="w-full h-48 object-cover rounded-t-xl"
                      loading="lazy"
                    />
                    {/* Type Badge */}
                    <span className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold text-[var(--text-black)] shadow-lg ${
                      property.type === 'rental' ? 'bg-[var(--bg-black)]' :
                      property.type === 'property' ? 'bg-[var(--bg-black)]' :
                      'bg-purple-500'
                    }`}>
                      {property.type === 'rental' 
                        ? t('profile.property.types.rental')
                        : property.type === 'property'
                          ? t('profile.property.types.property')
                          : t('profile.property.types.hotel')
                      }
                    </span>
                    {/* Price Badge */}
                    <span className="absolute top-3 left-32 px-3 py-1 rounded-lg text-sm font-bold text-[var(--text-black)] bg-[var(--bg-black)] backdrop-blur-sm">
                      {formatPrice(property.price_rwf)}
                    </span>
                    {/* Like Button */}
                    <div className="absolute bottom-3 right-3 flex items-center gap-1 bg-black/60 backdrop-blur-sm px-2.5 py-1.5 rounded-full text-white">
                      <Heart
                        size={16}
                        className={`${property.is_liked ? 'fill-pink-500 text-pink-500' : 'text-white'}`}
                      />
                      <span className="text-xs font-medium">
                        {formatNumber(property.likes_count)}
                      </span>
                    </div>
                  </div>

                  {/* User Header */}
                  <div className="flex items-center justify-between p-4 border-b border-gray-700">
                    <div className="flex items-center gap-3">
                      <img
                        src={property.owner?.avatar || `https://ui-avatars.com/api/?name=${property.owner?.username?.[0] || 'U'}&background=ff6b6b&color=fff`}
                        alt={property.owner?.username || 'User'}
                        className="w-10 h-10 rounded-full ring-2 ring-pink-500"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-[var(--text-black)] text-sm">
                            {property.owner?.username || t('profile.property.details.unknownUser')}
                          </h3>
                          {property.owner?.verified && (
                            <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                            </svg>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 flex items-center gap-1">
                          <Clock size={12} />
                          {property.posted_at || 'Just now'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Property Details */}
                  <div className="p-4">
                    <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
                      <div className="flex items-center gap-1">
                        <MapPin size={16} className="text-pink-500" />
                        <span className="text-[var(--text-black)]">{property.location}</span>
                      </div>
                      {property.bedrooms > 0 && (
                        <div className="flex items-center gap-1 text-[var(--text-black)]">
                          <BedDouble size={16} />
                          <span>{t('profile.property.details.bedrooms', { count: property.bedrooms })}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 sm:hidden bg-[var(--bg-black)] backdrop-blur-xl border-t border-gray-200 dark:border-gray-700 px-4 py-2 z-30">
        <div className="flex justify-around items-center text-[var(--text-black)]">
          {[
            { to: '/homedash', icon: <FaHome size={16} />, label: t('profile.navigation.home') },
            { to: '/exploredash', icon: <Explore size={16} />, label: t('profile.navigation.explore') },
            { to: '/uploaddash', icon: <FaPlusSquare size={16} />, label: t('profile.navigation.upload') },
            { to: '/profiledash', icon: <FaUser size={16} />, label: t('profile.navigation.profile') }
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="flex flex-col items-center py-2 px-2 group no-underline"
            >
              <div className={`transition-colors duration-200 ${
                location.pathname === item.to
                  ? 'text-pink-500'
                  : 'text-[var(--text-black)]'
              }`}>
                {item.icon}
              </div>
              <span className={`text-xs mt-1 transition-colors duration-200 ${
                location.pathname === item.to
                  ? 'text-pink-500 font-medium'
                  : 'text-[var(--text-black)]'
              }`}>
                {item.label}
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* Property Modal */}
      {user && (
        <PropertyModal
          property={selectedProperty}
          properties={properties}
          initialIndex={properties.findIndex(p => p.id === selectedProperty?.id)}
          isOpen={isModalOpen}
          onClose={closePropertyModal}
          onMessageOwner={handleMessageOwner}
        />
      )}
    </div>
  );
}